import React, { useCallback, useEffect, useState } from 'react'
import {
  Alert, Autocomplete, Box, Button, Card, CardContent, Checkbox, Chip, CircularProgress, Dialog, DialogActions,
  DialogContent, DialogTitle, Divider, FormControlLabel, Grid, IconButton, MenuItem, Paper, Stack, Switch, Tab,
  Table, TableBody, TableCell, TableContainer, TableHead, TablePagination, TableRow, Tabs, TextField, Tooltip, Typography,
} from '@mui/material'
import {
  Add as AddIcon, Delete as DeleteIcon, Edit as EditIcon, StopCircleOutlined as StopIcon, Visibility as ViewIcon,
} from '@mui/icons-material'
import { useSnackbar } from 'notistack'
import PageHeader from '../components/ui/PageHeader'
import { usePermissions } from '../hooks/usePermissions'
import { productService, categoryService } from '../services/productService'
import { CustomerService } from '../services/customerService'
import {
  BenefitType, Deal, DealInput, DealRedemptionRow, DealTier, fromLocalInput, promotionService, STATE_COLOR, toLocalInput,
} from '../services/promotionService'
import { getErrorMessage } from '../utils/errorHandlers'

const inr = (n: number) => `₹${n.toLocaleString('en-IN', { maximumFractionDigits: 2 })}`
const when = (iso: string) => new Date(iso).toLocaleString('en-IN', { day: '2-digit', month: 'short', year: '2-digit', hour: '2-digit', minute: '2-digit' })

const TYPE_LABEL: Record<Deal['deal_type'], string> = {
  seasonal: 'Seasonal', cashback: 'Cashback', free_gift: 'Free gift', reseller_scheme: 'Reseller scheme',
}
const TYPE_HELP: Record<Deal['deal_type'], string> = {
  seasonal: 'A festive or seasonal offer banner. Add rules to give cashback or gifts, or leave them empty to just announce it.',
  cashback: 'Cashback credited to the customer’s account after delivery, e.g. ₹500 on orders above ₹15,000.',
  free_gift: 'A free product added to the order, e.g. buy 10 bottles and get 1 free.',
  reseller_scheme: 'A volume scheme for resellers: higher spend or quantity unlocks bigger rewards.',
}
const STATE_TABS = [
  { value: 'live', label: 'Live' }, { value: 'scheduled', label: 'Scheduled' }, { value: 'ended', label: 'Ended' },
  { value: 'paused', label: 'Paused' }, { value: '', label: 'All' },
]
const CUSTOMER_TYPES = ['RETAIL', 'RESELLER', 'WHOLESALER', 'BROKER']

const describeTier = (t: DealTier) => {
  const when_ = t.threshold_type === 'order_value' ? `Order ${inr(t.threshold)}+` : `Buy ${t.threshold}+`
  const b = t.benefit
  const what = b.type === 'gift' ? `free ${b.gift_name || 'gift'} × ${b.gift_quantity || 1}`
    : b.type === 'cashback_fixed' ? `${inr(b.value || 0)} cashback`
      : `${b.value}% cashback${b.max_amount ? ` (max ${inr(b.max_amount)})` : ''}`
  return `${when_} → ${what}`
}

interface TierForm {
  threshold_type: 'order_value' | 'quantity'
  threshold: string
  benefit_type: BenefitType
  value: string
  max_amount: string
  gift: { id: string; label: string } | null
  gift_quantity: string
}

interface FormState {
  name: string
  deal_type: Deal['deal_type']
  season_tag: string
  description: string
  terms: string
  starts_at: string
  ends_at: string
  show_on_portal: boolean
  is_active: boolean
  priority: string
  stackable: boolean
  audience_type: Deal['audience_type']
  audience_ids: string[]
  scope_type: Deal['scope_type']
  scope_items: { id: string; name: string }[]
  tiers: TierForm[]
  repeatable: boolean
  max_redemptions: string
  per_customer_limit: string
}

const blankTier = (deal_type: Deal['deal_type']): TierForm => ({
  threshold_type: deal_type === 'free_gift' ? 'quantity' : 'order_value', threshold: '',
  benefit_type: deal_type === 'free_gift' ? 'gift' : 'cashback_fixed', value: '', max_amount: '', gift: null, gift_quantity: '1',
})

const blankForm = (): FormState => {
  const s = new Date(); const e = new Date(s.getTime() + 14 * 86400000)
  return {
    name: '', deal_type: 'cashback', season_tag: '', description: '', terms: '', starts_at: toLocalInput(s.toISOString()),
    ends_at: toLocalInput(e.toISOString()), show_on_portal: true, is_active: true, priority: '0', stackable: true,
    audience_type: 'all', audience_ids: [], scope_type: 'order', scope_items: [], tiers: [blankTier('cashback')],
    repeatable: false, max_redemptions: '', per_customer_limit: '',
  }
}

const fromDeal = (d: Deal): FormState => ({
  name: d.name, deal_type: d.deal_type, season_tag: d.season_tag || '', description: d.description || '', terms: d.terms || '',
  starts_at: toLocalInput(d.starts_at), ends_at: toLocalInput(d.ends_at), show_on_portal: d.show_on_portal, is_active: d.is_active,
  priority: String(d.priority), stackable: d.stackable, audience_type: d.audience_type, audience_ids: d.audience_ids,
  scope_type: d.scope_type, scope_items: d.scope_items, repeatable: d.repeatable,
  max_redemptions: d.max_redemptions ? String(d.max_redemptions) : '', per_customer_limit: d.per_customer_limit ? String(d.per_customer_limit) : '',
  tiers: d.tiers.map((t) => ({
    threshold_type: t.threshold_type, threshold: String(t.threshold), benefit_type: t.benefit.type,
    value: t.benefit.value != null ? String(t.benefit.value) : '', max_amount: t.benefit.max_amount ? String(t.benefit.max_amount) : '',
    gift: t.benefit.gift_variant_id ? { id: t.benefit.gift_variant_id, label: t.benefit.gift_name || 'Gift item' } : null,
    gift_quantity: String(t.benefit.gift_quantity || 1),
  })),
})

const DealsPage: React.FC = () => {
  const { enqueueSnackbar } = useSnackbar()
  const { hasPermission } = usePermissions()
  const canWrite = hasPermission('promotions.write')

  const [state, setState] = useState('live')
  const [page, setPage] = useState(0)
  const [pageSize, setPageSize] = useState(25)
  const [rows, setRows] = useState<Deal[]>([])
  const [total, setTotal] = useState(0)
  const [loading, setLoading] = useState(false)

  const [open, setOpen] = useState(false)
  const [editing, setEditing] = useState<Deal | null>(null)
  const [form, setForm] = useState<FormState>(blankForm())
  const [saving, setSaving] = useState(false)
  const [error, setError] = useState('')

  const [groups, setGroups] = useState<{ id: string; name: string }[]>([])
  const [scopeOptions, setScopeOptions] = useState<{ id: string; name: string }[]>([])
  const [scopeQuery, setScopeQuery] = useState('')
  const [giftOptions, setGiftOptions] = useState<{ id: string; label: string }[]>([])
  const [giftQuery, setGiftQuery] = useState('')

  const [detail, setDetail] = useState<Deal | null>(null)
  const [detailRows, setDetailRows] = useState<DealRedemptionRow[]>([])

  const load = useCallback(async () => {
    setLoading(true)
    try {
      const res = await promotionService.listDeals({ state: state || undefined, page: page + 1, page_size: pageSize })
      setRows(res.items); setTotal(res.total)
    } catch (err) {
      enqueueSnackbar(getErrorMessage(err, 'Failed to load deals'), { variant: 'error' })
    } finally { setLoading(false) }
  }, [state, page, pageSize, enqueueSnackbar])

  useEffect(() => { load() }, [load])

  useEffect(() => {
    if (!open || groups.length) return
    CustomerService.getCustomerGroups({ per_page: 100 }).then((r) => setGroups(r.items.map((g) => ({ id: g.id, name: g.name })))).catch((): void => undefined)
  }, [open, groups.length])

  useEffect(() => {
    if (!open || form.scope_type === 'order') return
    const handle = setTimeout(async () => {
      try {
        if (form.scope_type === 'products') {
          if (scopeQuery.trim().length < 2) return
          const res = await productService.listProducts({ search: scopeQuery.trim(), per_page: 20 })
          setScopeOptions(res.items.map((p) => ({ id: p.id, name: p.name })))
        } else {
          const res = await categoryService.listCategories({ per_page: 100 })
          setScopeOptions(res.items.map((c) => ({ id: c.id, name: c.name })))
        }
      } catch { /* leave options as they are */ }
    }, 250)
    return () => clearTimeout(handle)
  }, [open, form.scope_type, scopeQuery])

  useEffect(() => {
    if (!open || giftQuery.trim().length < 2) return
    const handle = setTimeout(async () => {
      try {
        const res = await promotionService.searchVariants(giftQuery.trim())
        setGiftOptions(res.map((v) => ({ id: v.id, label: v.name && v.name.toLowerCase() !== 'default' ? `${v.product_name} – ${v.name}` : v.product_name })))
      } catch { /* ignore */ }
    }, 300)
    return () => clearTimeout(handle)
  }, [open, giftQuery])

  const openCreate = () => { setEditing(null); setForm(blankForm()); setError(''); setOpen(true) }
  const openEdit = (d: Deal) => { setEditing(d); setForm(fromDeal(d)); setError(''); setOpen(true) }

  const setTier = (i: number, patch: Partial<TierForm>) =>
    setForm((f) => ({ ...f, tiers: f.tiers.map((t, idx) => (idx === i ? { ...t, ...patch } : t)) }))

  const changeType = (deal_type: Deal['deal_type']) =>
    setForm((f) => ({
      ...f, deal_type,
      audience_type: deal_type === 'reseller_scheme' && f.audience_type === 'all' ? 'customer_types' : f.audience_type,
      audience_ids: deal_type === 'reseller_scheme' && f.audience_type === 'all' ? ['RESELLER'] : f.audience_ids,
      tiers: f.tiers.length ? f.tiers : (deal_type === 'seasonal' ? [] : [blankTier(deal_type)]),
    }))

  const buildPayload = (): DealInput | string => {
    if (form.name.trim().length < 3) return 'Give the deal a name (at least 3 letters).'
    if (!form.starts_at || !form.ends_at || new Date(form.ends_at) <= new Date(form.starts_at)) return 'The end must be after the start.'
    if (form.scope_type !== 'order' && !form.scope_items.length) return 'Choose the products or categories this deal covers.'
    if (form.audience_type !== 'all' && !form.audience_ids.length) return 'Choose who the deal is for.'
    if (form.deal_type !== 'seasonal' && !form.tiers.length) return 'Add at least one rule.'
    const tiers: DealTier[] = []
    for (let i = 0; i < form.tiers.length; i++) {
      const t = form.tiers[i]
      const threshold = Number(t.threshold)
      if (!threshold || threshold <= 0) return `Rule ${i + 1}: enter the spend or quantity needed.`
      if (t.benefit_type === 'gift') {
        if (!t.gift) return `Rule ${i + 1}: choose the free gift product.`
        tiers.push({ threshold_type: t.threshold_type, threshold, benefit: { type: 'gift', gift_variant_id: t.gift.id, gift_quantity: Number(t.gift_quantity) || 1 } })
      } else {
        const value = Number(t.value)
        if (!value || value <= 0) return `Rule ${i + 1}: enter the cashback amount.`
        if (t.benefit_type === 'cashback_percent' && value > 100) return `Rule ${i + 1}: percent cannot exceed 100.`
        tiers.push({ threshold_type: t.threshold_type, threshold, benefit: { type: t.benefit_type, value, ...(t.max_amount ? { max_amount: Number(t.max_amount) } : {}) } })
      }
    }
    return {
      name: form.name.trim(), deal_type: form.deal_type, season_tag: form.season_tag.trim() || null, description: form.description.trim() || null,
      terms: form.terms.trim() || null, banner_url: editing?.banner_url || null, starts_at: fromLocalInput(form.starts_at), ends_at: fromLocalInput(form.ends_at),
      is_active: form.is_active, show_on_portal: form.show_on_portal, priority: Number(form.priority) || 0, stackable: form.stackable,
      audience_type: form.audience_type, audience_ids: form.audience_type === 'all' ? [] : form.audience_ids,
      scope_type: form.scope_type, scope_ids: form.scope_type === 'order' ? [] : form.scope_items.map((s) => s.id),
      tiers, repeatable: form.repeatable,
      max_redemptions: form.max_redemptions ? Number(form.max_redemptions) : null,
      per_customer_limit: form.per_customer_limit ? Number(form.per_customer_limit) : null,
    }
  }

  const save = async () => {
    const payload = buildPayload()
    if (typeof payload === 'string') { setError(payload); return }
    setSaving(true); setError('')
    try {
      if (editing) await promotionService.updateDeal(editing.id, payload)
      else await promotionService.createDeal(payload)
      enqueueSnackbar(editing ? 'Deal updated' : 'Deal created', { variant: 'success' })
      setOpen(false); load()
    } catch (err) {
      setError(getErrorMessage(err, 'Could not save the deal'))
    } finally { setSaving(false) }
  }

  const act = async (fn: () => Promise<unknown>, done: string) => {
    try { await fn(); enqueueSnackbar(done, { variant: 'success' }); load() }
    catch (err) { enqueueSnackbar(getErrorMessage(err, 'Action failed'), { variant: 'error' }) }
  }

  const openDetail = async (d: Deal) => {
    try {
      setDetail(await promotionService.getDeal(d.id))
      setDetailRows((await promotionService.dealRedemptions(d.id)).items)
    } catch (err) { enqueueSnackbar(getErrorMessage(err, 'Could not load the deal'), { variant: 'error' }) }
  }

  const showsRules = form.deal_type !== 'seasonal' || form.tiers.length > 0

  return (
    <Box>
      <PageHeader
        title="Deals & Schemes"
        subtitle="Seasonal offers, cashback, free gifts and reseller schemes with a start and end date."
        breadcrumbs={[{ label: 'Promotions' }, { label: 'Deals & Schemes' }]}
        actions={canWrite ? [{ label: 'New deal', onClick: openCreate, icon: <AddIcon />, variant: 'contained' }] : []}
      />
      <Card>
        <CardContent>
          <Tabs value={state} onChange={(_, v) => { setState(v); setPage(0) }} variant="scrollable" scrollButtons="auto" sx={{ mb: 2 }}>
            {STATE_TABS.map((t) => <Tab key={t.label} value={t.value} label={t.label} />)}
          </Tabs>
          <TableContainer component={Paper} variant="outlined">
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell>Deal</TableCell><TableCell>Rules</TableCell><TableCell>For</TableCell><TableCell>Runs</TableCell>
                  <TableCell align="right">Given</TableCell><TableCell>Status</TableCell><TableCell align="right">Actions</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {loading && <TableRow><TableCell colSpan={7} align="center"><CircularProgress size={22} /></TableCell></TableRow>}
                {!loading && rows.length === 0 && (
                  <TableRow><TableCell colSpan={7} align="center" sx={{ py: 5, color: 'text.secondary' }}>No deals here yet.</TableCell></TableRow>
                )}
                {rows.map((d) => (
                  <TableRow key={d.id} hover>
                    <TableCell>
                      <Typography variant="body2" fontWeight={600}>{d.name}</Typography>
                      <Stack direction="row" spacing={0.5} sx={{ mt: 0.5 }}>
                        <Chip size="small" variant="outlined" label={TYPE_LABEL[d.deal_type]} />
                        {d.season_tag && <Chip size="small" variant="outlined" label={d.season_tag} />}
                        {!d.show_on_portal && <Chip size="small" label="hidden" />}
                      </Stack>
                    </TableCell>
                    <TableCell sx={{ maxWidth: 300 }}>
                      {d.tiers.length ? d.tiers.map((t, i) => <Typography key={i} variant="caption" display="block">{describeTier(t)}</Typography>) : <Typography variant="caption" color="text.secondary">Announcement only</Typography>}
                      {d.scope_type !== 'order' && <Typography variant="caption" color="text.secondary">On {d.scope_items.map((s) => s.name).slice(0, 2).join(', ')}{d.scope_items.length > 2 ? ` +${d.scope_items.length - 2}` : ''}</Typography>}
                    </TableCell>
                    <TableCell><Typography variant="caption">{d.audience_type === 'all' ? 'Everyone' : d.audience_type === 'customer_types' ? d.audience_ids.join(', ') : `${d.audience_ids.length} group(s)`}</Typography></TableCell>
                    <TableCell><Typography variant="caption" display="block">{when(d.starts_at)}</Typography><Typography variant="caption" display="block">to {when(d.ends_at)}</Typography></TableCell>
                    <TableCell align="right">{d.redemption_count}{d.max_redemptions ? ` / ${d.max_redemptions}` : ''}</TableCell>
                    <TableCell><Chip size="small" label={d.state} color={STATE_COLOR[d.state]} /></TableCell>
                    <TableCell align="right">
                      <Tooltip title="Results"><IconButton size="small" onClick={() => openDetail(d)}><ViewIcon fontSize="small" /></IconButton></Tooltip>
                      {canWrite && <Tooltip title="Edit"><IconButton size="small" onClick={() => openEdit(d)}><EditIcon fontSize="small" /></IconButton></Tooltip>}
                      {canWrite && (d.state === 'live' || d.state === 'scheduled') && (
                        <Tooltip title="Stop now"><IconButton size="small" color="warning" onClick={() => window.confirm('Stop this deal now? Orders placed from now on will not get it.') && act(() => promotionService.stopDeal(d.id), 'Deal stopped')}><StopIcon fontSize="small" /></IconButton></Tooltip>
                      )}
                      {canWrite && d.redemption_count === 0 && (
                        <Tooltip title="Delete"><IconButton size="small" color="error" onClick={() => window.confirm('Delete this deal?') && act(() => promotionService.deleteDeal(d.id), 'Deal deleted')}><DeleteIcon fontSize="small" /></IconButton></Tooltip>
                      )}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
            <TablePagination component="div" count={total} page={page} rowsPerPage={pageSize}
              onPageChange={(_, p) => setPage(p)} onRowsPerPageChange={(e) => { setPageSize(Number(e.target.value)); setPage(0) }} rowsPerPageOptions={[10, 25, 50]} />
          </TableContainer>
        </CardContent>
      </Card>

      {/* ---------- create / edit ---------- */}
      <Dialog open={open} onClose={() => !saving && setOpen(false)} maxWidth="md" fullWidth>
        <DialogTitle>{editing ? 'Edit deal' : 'New deal'}</DialogTitle>
        <DialogContent dividers>
          <Grid container spacing={2}>
            {error && <Grid item xs={12}><Alert severity="error">{error}</Alert></Grid>}
            <Grid item xs={12} sm={7}><TextField fullWidth label="Deal name" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} placeholder="e.g. Diwali Bonanza" /></Grid>
            <Grid item xs={12} sm={5}>
              <TextField select fullWidth label="Type" value={form.deal_type} onChange={(e) => changeType(e.target.value as Deal['deal_type'])}>
                {(Object.keys(TYPE_LABEL) as Deal['deal_type'][]).map((k) => <MenuItem key={k} value={k}>{TYPE_LABEL[k]}</MenuItem>)}
              </TextField>
            </Grid>
            <Grid item xs={12}><Alert severity="info" icon={false}>{TYPE_HELP[form.deal_type]}</Alert></Grid>
            <Grid item xs={12} sm={6}><TextField fullWidth type="datetime-local" label="Starts" InputLabelProps={{ shrink: true }} value={form.starts_at} onChange={(e) => setForm({ ...form, starts_at: e.target.value })} /></Grid>
            <Grid item xs={12} sm={6}><TextField fullWidth type="datetime-local" label="Ends" InputLabelProps={{ shrink: true }} value={form.ends_at} onChange={(e) => setForm({ ...form, ends_at: e.target.value })} /></Grid>
            <Grid item xs={12} sm={4}><TextField fullWidth label="Season tag (optional)" placeholder="diwali, wedding-season" value={form.season_tag} onChange={(e) => setForm({ ...form, season_tag: e.target.value })} /></Grid>
            <Grid item xs={12} sm={8}><TextField fullWidth label="Short description shown to customers" value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} /></Grid>

            <Grid item xs={12}><Divider><Chip size="small" label="Who gets it" /></Divider></Grid>
            <Grid item xs={12} sm={5}>
              <TextField select fullWidth label="Audience" value={form.audience_type} onChange={(e) => setForm({ ...form, audience_type: e.target.value as Deal['audience_type'], audience_ids: [] })}>
                <MenuItem value="all">Every customer</MenuItem>
                <MenuItem value="customer_types">Customer types (e.g. Resellers)</MenuItem>
                <MenuItem value="customer_groups">Specific customer groups</MenuItem>
              </TextField>
            </Grid>
            <Grid item xs={12} sm={7}>
              {form.audience_type === 'customer_types' && (
                <Autocomplete multiple options={CUSTOMER_TYPES} value={form.audience_ids} onChange={(_, v) => setForm({ ...form, audience_ids: v })}
                  renderInput={(p) => <TextField {...p} label="Customer types" />} />
              )}
              {form.audience_type === 'customer_groups' && (
                <Autocomplete multiple options={groups} getOptionLabel={(g) => g.name} isOptionEqualToValue={(a, b) => a.id === b.id}
                  value={groups.filter((g) => form.audience_ids.includes(g.id))} onChange={(_, v) => setForm({ ...form, audience_ids: v.map((g) => g.id) })}
                  renderInput={(p) => <TextField {...p} label="Customer groups" />} />
              )}
            </Grid>

            <Grid item xs={12}><Divider><Chip size="small" label="What counts" /></Divider></Grid>
            <Grid item xs={12} sm={5}>
              <TextField select fullWidth label="Counts toward the rule" value={form.scope_type}
                onChange={(e) => { setForm({ ...form, scope_type: e.target.value as Deal['scope_type'], scope_items: [] }); setScopeOptions([]) }}>
                <MenuItem value="order">Whole order</MenuItem>
                <MenuItem value="products">Specific products</MenuItem>
                <MenuItem value="categories">Specific categories</MenuItem>
              </TextField>
            </Grid>
            <Grid item xs={12} sm={7}>
              {form.scope_type !== 'order' && (
                <Autocomplete multiple options={scopeOptions} getOptionLabel={(o) => o.name} isOptionEqualToValue={(a, b) => a.id === b.id}
                  value={form.scope_items} onChange={(_, v) => setForm({ ...form, scope_items: v })} onInputChange={(_, v) => setScopeQuery(v)} filterOptions={(x) => x}
                  renderInput={(p) => <TextField {...p} label={form.scope_type === 'products' ? 'Products (type to search)' : 'Categories'} />} />
              )}
            </Grid>

            {showsRules && (
              <>
                <Grid item xs={12}><Divider><Chip size="small" label="Rules — the highest rule the customer reaches applies" /></Divider></Grid>
                {form.tiers.map((t, i) => (
                  <Grid item xs={12} key={i}>
                    <Paper variant="outlined" sx={{ p: 1.5 }}>
                      <Grid container spacing={1.5} alignItems="center">
                        <Grid item xs={12} sm={3}>
                          <TextField select fullWidth size="small" label="When" value={t.threshold_type} onChange={(e) => setTier(i, { threshold_type: e.target.value as TierForm['threshold_type'] })}>
                            <MenuItem value="order_value">Spend reaches (₹, before GST)</MenuItem>
                            <MenuItem value="quantity">Quantity reaches</MenuItem>
                          </TextField>
                        </Grid>
                        <Grid item xs={6} sm={2}><TextField fullWidth size="small" type="number" label={t.threshold_type === 'order_value' ? 'Amount ₹' : 'Units'} value={t.threshold} onChange={(e) => setTier(i, { threshold: e.target.value })} /></Grid>
                        <Grid item xs={6} sm={3}>
                          <TextField select fullWidth size="small" label="Customer gets" value={t.benefit_type} onChange={(e) => setTier(i, { benefit_type: e.target.value as BenefitType })}>
                            <MenuItem value="cashback_fixed">Fixed cashback ₹</MenuItem>
                            <MenuItem value="cashback_percent">Cashback %</MenuItem>
                            <MenuItem value="gift">Free gift</MenuItem>
                          </TextField>
                        </Grid>
                        {t.benefit_type === 'gift' ? (
                          <>
                            <Grid item xs={8} sm={3}>
                              <Autocomplete size="small" options={giftOptions} getOptionLabel={(o) => o.label} isOptionEqualToValue={(a, b) => a.id === b.id}
                                value={t.gift} onChange={(_, v) => setTier(i, { gift: v })} onInputChange={(_, v) => setGiftQuery(v)} filterOptions={(x) => x}
                                renderInput={(p) => <TextField {...p} label="Gift product" />} />
                            </Grid>
                            <Grid item xs={4} sm={1}><TextField fullWidth size="small" type="number" label="Qty" value={t.gift_quantity} onChange={(e) => setTier(i, { gift_quantity: e.target.value })} /></Grid>
                          </>
                        ) : (
                          <>
                            <Grid item xs={6} sm={2}><TextField fullWidth size="small" type="number" label={t.benefit_type === 'cashback_percent' ? 'Percent' : 'Amount ₹'} value={t.value} onChange={(e) => setTier(i, { value: e.target.value })} /></Grid>
                            <Grid item xs={5} sm={1}>{t.benefit_type === 'cashback_percent' && <TextField fullWidth size="small" type="number" label="Max ₹" value={t.max_amount} onChange={(e) => setTier(i, { max_amount: e.target.value })} />}</Grid>
                          </>
                        )}
                        <Grid item xs={1}><IconButton size="small" onClick={() => setForm({ ...form, tiers: form.tiers.filter((_, idx) => idx !== i) })}><DeleteIcon fontSize="small" /></IconButton></Grid>
                      </Grid>
                    </Paper>
                  </Grid>
                ))}
                <Grid item xs={12}>
                  <Button size="small" startIcon={<AddIcon />} onClick={() => setForm({ ...form, tiers: [...form.tiers, blankTier(form.deal_type)] })}>Add another rule</Button>
                  <FormControlLabel sx={{ ml: 2 }} control={<Checkbox checked={form.repeatable} onChange={(e) => setForm({ ...form, repeatable: e.target.checked })} />}
                    label="Repeat for every multiple (e.g. every 10 units earns another gift)" />
                </Grid>
              </>
            )}

            <Grid item xs={12}><Divider><Chip size="small" label="Limits & visibility" /></Divider></Grid>
            <Grid item xs={6} sm={3}><TextField fullWidth type="number" label="Total uses (optional)" value={form.max_redemptions} onChange={(e) => setForm({ ...form, max_redemptions: e.target.value })} /></Grid>
            <Grid item xs={6} sm={3}><TextField fullWidth type="number" label="Per customer (optional)" value={form.per_customer_limit} onChange={(e) => setForm({ ...form, per_customer_limit: e.target.value })} /></Grid>
            <Grid item xs={6} sm={2}><TextField fullWidth type="number" label="Priority" value={form.priority} onChange={(e) => setForm({ ...form, priority: e.target.value })} helperText="Higher first" /></Grid>
            <Grid item xs={12} sm={4}>
              <FormControlLabel control={<Switch checked={form.show_on_portal} onChange={(e) => setForm({ ...form, show_on_portal: e.target.checked })} />} label="Show on customer portal" />
              <FormControlLabel control={<Switch checked={form.stackable} onChange={(e) => setForm({ ...form, stackable: e.target.checked })} />} label="Can combine with other deals" />
              <FormControlLabel control={<Switch checked={form.is_active} onChange={(e) => setForm({ ...form, is_active: e.target.checked })} />} label="Active" />
            </Grid>
            <Grid item xs={12}><TextField fullWidth multiline minRows={2} label="Terms & conditions (shown to customers)" value={form.terms} onChange={(e) => setForm({ ...form, terms: e.target.value })} /></Grid>
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOpen(false)} disabled={saving}>Cancel</Button>
          <Button variant="contained" onClick={save} disabled={saving}>{saving ? 'Saving…' : editing ? 'Save changes' : 'Create deal'}</Button>
        </DialogActions>
      </Dialog>

      {/* ---------- results ---------- */}
      <Dialog open={!!detail} onClose={() => setDetail(null)} maxWidth="md" fullWidth>
        <DialogTitle>{detail?.name} — results</DialogTitle>
        <DialogContent dividers>
          {detail?.stats && Object.keys(detail.stats).length > 0 ? (
            <Stack direction="row" spacing={1} sx={{ mb: 2, flexWrap: 'wrap', rowGap: 1 }}>
              {Object.entries(detail.stats).map(([k, v]) => <Chip key={k} label={`${k.replace('_', ' ')}: ${v.count}${v.amount ? ` · ${inr(v.amount)}` : ''}`} />)}
            </Stack>
          ) : <Typography color="text.secondary" sx={{ mb: 2 }}>No orders have received this deal yet.</Typography>}
          {detailRows.length > 0 && (
            <Table size="small">
              <TableHead><TableRow><TableCell>Customer</TableCell><TableCell>Order</TableCell><TableCell>Benefit</TableCell><TableCell>Status</TableCell><TableCell align="right">Action</TableCell></TableRow></TableHead>
              <TableBody>
                {detailRows.map((r) => (
                  <TableRow key={r.id}>
                    <TableCell>{r.customer_name}</TableCell><TableCell>{r.order_number}</TableCell><TableCell>{r.description}</TableCell>
                    <TableCell><Chip size="small" label={r.status.replace('_', ' ')} color={r.status === 'credited' ? 'success' : r.status === 'void' || r.status === 'reversed' ? 'default' : 'info'} /></TableCell>
                    <TableCell align="right">
                      {canWrite && r.status === 'credited' && (
                        <Button size="small" color="warning" onClick={async () => {
                          if (!window.confirm(`Take back ${inr(r.amount)} cashback from ${r.customer_name}?`)) return
                          try { await promotionService.reverseRedemption(r.id); enqueueSnackbar('Cashback reversed', { variant: 'success' }); setDetailRows((await promotionService.dealRedemptions(detail!.id)).items) }
                          catch (err) { enqueueSnackbar(getErrorMessage(err, 'Could not reverse'), { variant: 'error' }) }
                        }}>Reverse</Button>
                      )}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </DialogContent>
        <DialogActions><Button onClick={() => setDetail(null)}>Close</Button></DialogActions>
      </Dialog>
    </Box>
  )
}

export default DealsPage
